import React from 'react';
import { TextField, InputAdornment } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import './textInput.scss';
/**
 * Generic input component
 * @param {String} placeholder Text to display as placeholder  
 * @param {String} label Text to display as label  
 * @param {Function} onChange Callback to execute when the onChange event is fired
 * @param {Function} onClearButtonClick Callback to execute when pressing the clear icon
 * @param {String} value String value of the input
 * @param {Boolean} searchIcon If true, shows a search icon
 * @param {String|Int} width Width value to modify the input
 * @param {String} name input name
 * @param {Function} onClick event Callback function
 * @param {Boolean} multiline boolean value to show multiple input rows
 * @param {Boolean} disabled boolean value to disable fields
 * @param {Boolean} required boolean value to make fields required
 * @param {String} type input type
 * @param {Int} maxLength maximum input value length
 * @param {Function} onBlur event Callback function
 * @returns {Component} JSX element
 */
export const TextInput = ({
    placeholder = '',
    onChange,
    onClearButtonClick,
    value = '',
    searchIcon,
    label = '',
    width,
    name,
    onClick,
    multiline = false,
    rows = 0,
    disabled = false,
    type = 'text',
    maxLength,
    required,
    customProps,
    inputProps,
    shrink = 'false',
    onBlur,
    currency = false,
    onKeyUp = () => { },
    currencyAllowNegative,
    ...props
}) => {

    const startAdornment = currency
        ? <InputAdornment position="start">$</InputAdornment>
        : customProps?.startAdornment || null;

    const handleCurrencyChange = (e) => {
        const rawValue = e.target.value.replace(/\./g, '');

        const pattern = currencyAllowNegative
            ? /^-?\d*,?\d{0,2}$/
            : /^\d*,?\d{0,2}$/;

        if (pattern.test(rawValue)) {
            onChange({
                ...e,
                target: {
                    ...e.target,
                    name,
                    value: rawValue
                }
            });
        }
    };

    const renderCurrencyValue = () => {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return (
        <TextField
            fullWidth={!width}
            style={width && { width: width }}
            className={props.longLabel ? 'long-label text-input' : 'text-input'}
            size='small'
            placeholder={placeholder}
            onChange={currency ? handleCurrencyChange : onChange}
            value={currency ? renderCurrencyValue() : value}
            onKeyUp={onKeyUp}
            label={label}
            type={type}
            name={name}
            onBlur={onBlur}
            required={required}
            onClick={!disabled ? onClick : null}
            multiline={multiline}
            rows={rows}
            disabled={disabled}
            InputLabelProps={props.longLabel ? {
                shrink: true,
                sx: {
                    overflow: 'visible',
                    textOverflow: 'clip',
                    maxWidth: '100%',
                },
            } : {}}
            InputProps={{
                shrink: shrink,
                startAdornment: startAdornment,
                ...customProps,
                endAdornment: (
                    <InputAdornment position="end">
                        {searchIcon && (
                            <SearchIcon className='search-icon' />
                        )}
                        {Boolean(onClearButtonClick) && (
                            <ClearIcon className='clear-icon' onClick={onClearButtonClick} />
                        )}
                    </InputAdornment>
                )
            }}
            inputProps={{
                ...inputProps,
                maxLength: maxLength,
            }}
            {...props}
        />
    )
}